import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useSession, type ClientScope } from '@/hooks/useSession'
import { PrincipalPicker } from '@/components/PrincipalPicker'
import { pt } from '@/i18n/pt-PT'

// Guard for the /portal/* CLIENT-portal subtree. Only an `authenticated_client` principal may enter;
// staff bounce to /operacoes, everyone else to /login or an unprovisioned message. The real access
// control is RLS — this is UX so external users get a clean redirect, never a staff screen.
export function RequireClient({ children }: { children: (client: ClientScope) => ReactNode }) {
  const session = useSession()

  if (session.status === 'loading') {
    return <FullscreenMessage>{pt.auth.loading}</FullscreenMessage>
  }
  if (
    session.status === 'unauthenticated' ||
    session.status === 'needs_mfa_enroll' ||
    session.status === 'needs_mfa_challenge'
  ) {
    return <Navigate to={{ pathname: '/login', search: window.location.search }} replace />
  }
  if (session.status === 'needs_principal_choice') {
    return <PrincipalPicker principals={session.principals} />
  }
  if (session.status === 'authenticated') {
    // The ACTIVE principal is staff. Offer the switch when this person also holds a client
    // principal — bouncing unconditionally deadlocks against RequireRole, which bounces back.
    const client = session.principals.filter((p) => p.kind === 'client')
    if (client.length > 0) return <PrincipalPicker principals={session.principals} />
    return <Navigate to="/operacoes" replace />
  }
  if (session.status === 'unprovisioned') {
    return <FullscreenMessage>{pt.auth.unprovisioned}</FullscreenMessage>
  }
  return <>{children(session.client)}</>
}

function FullscreenMessage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8 text-gray-700">
      <p className="rounded-lg border border-DEFAULT bg-surface px-6 py-4 text-[13px] shadow-card">
        {children}
      </p>
    </main>
  )
}
